import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Building, Euro, Target, TrendingUp, Award, Users } from 'lucide-react';

const Grants = () => {
  const { t } = useLanguage();

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-700 via-green-600 to-green-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        {/* Background Image - Scientist */}
        <div className="absolute inset-0 opacity-15">
          <img 
            src="https://customer-assets.emergentagent.com/job_metasigna-biotech/artifacts/mvmbygcj_hQhwnNgmo90NvG-1NrSa9.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {t(
              'Innovative Oncology Therapies: Next Generation Metabolic Oncology',
              'Terapias Oncológicas Innovadoras: Oncología Metabólica de Nueva Generación'
            )}
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-100 max-w-4xl">
            {t(
              'Development of investigational oncology drugs based on the metabolic oncology paradigm. Platform designed to achieve more selective tumor uptake with potentially greater efficacy and lower toxicity through dual-function therapeutic ligands. Subject to preclinical and clinical validation.',
              'Desarrollo de medicamentos oncológicos investigacionales basados en el paradigma de la oncología metabólica. Plataforma diseñada para lograr captación tumoral más selectiva con potencialmente mayor eficacia y menor toxicidad mediante ligandos terapéuticos de doble función. Sujeto a validación preclínica y clínica.'
            )}
          </p>

          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl font-bold text-[#FFD700]">$200B</div>
              <div className="text-sm text-gray-100">
                {t('Global Oncology Market 2030', 'Mercado Oncológico Mundial 2030')}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl font-bold text-[#FFD700]">Q1 2025</div>
              <div className="text-sm text-gray-100">{t('OEPM Patent Filed', 'Patente OEPM Presentada')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl font-bold text-[#FFD700]">UMA + CIC</div>
              <div className="text-sm text-gray-100">
                {t('Strategic Academic Alliances', 'Alianzas Académicas Estratégicas')}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl font-bold text-[#FFD700]">2025-2029</div>
              <div className="text-sm text-gray-100">{t('Development Roadmap', 'Hoja de Ruta Desarrollo')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-green-700">
            {t('Our Value Proposition', 'Nuestra Propuesta de Valor')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target size={32} className="text-green-700" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-green-700">
                {t('Enhanced Selectivity (Design Goal)', 'Mayor Selectividad (Objetivo de Diseño)')}
              </h3>
              <p className="text-gray-600">
                {t(
                  'Investigational platform designed to achieve more precise tumor accumulation. Selectivity validation ongoing in POC studies.',
                  'Plataforma investigacional diseñada para lograr acumulación tumoral más precisa. Validación de selectividad en curso en estudios POC.'
                )}
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award size={32} className="text-green-700" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-green-700">
                {t('Reduced Toxicity (Design Goal)', 'Menor Toxicidad (Objetivo de Diseño)')}
              </h3>
              <p className="text-gray-600">
                {t(
                  'Platform designed to reduce systemic exposure and side effects. Toxicity profile to be determined in preclinical studies.',
                  'Plataforma diseñada para reducir exposición sistémica y efectos secundarios. Perfil de toxicidad a determinar en estudios preclínicos.'
                )}
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp size={32} className="text-green-700" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-green-700">
                {t('Strategic Repositioning', 'Reposicionamiento Estratégico')}
              </h3>
              <p className="text-gray-600">
                {t(
                  'Modification of existing approved, orphan and nutraceutical drugs.',
                  'Modificación de fármacos aprobados, huérfanos y nutracéuticos existentes.'
                )}
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={32} className="text-green-700" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-green-700">
                {t('Improved Efficacy Potential', 'Potencial de Mayor Eficacia')}
              </h3>
              <p className="text-gray-600">
                {t(
                  'Investigational metabolic approach designed to enhance therapeutic outcomes. Efficacy to be validated.',
                  'Enfoque metabólico investigacional diseñado para mejorar resultados terapéuticos. Eficacia a validar.'
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Alignment */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-green-700">
            {t(
              'Strategic Alignment with European Priorities',
              'Alineación con Prioridades Estratégicas Europeas'
            )}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center space-x-4 mb-6">
                <Building size={40} className="text-green-700" />
                <h3 className="text-2xl font-bold text-green-700">CDTI</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-700 rounded-full mt-2"></div>
                  <span className="text-gray-700">
                    {t('Direct Innovation Line: Business R&D project financing', 'Línea Directa Innovación: Financiación de proyectos de I+D empresarial')}
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-700 rounded-full mt-2"></div>
                  <span className="text-gray-700">
                    {t('Science and Innovation Missions: Tractor projects in health', 'Misiones Ciencia e Innovación: Proyectos tractores en salud')}
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center space-x-4 mb-6">
                <Euro size={40} className="text-green-700" />
                <h3 className="text-2xl font-bold text-green-700">
                  {t('PERTE Cutting-Edge Health', 'PERTE Salud de Vanguardia')}
                </h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                {t(
                  'National strategic program to boost personalized medicine and advanced therapies in Spain',
                  'Programa estratégico nacional para impulsar la medicina personalizada y terapias avanzadas en España'
                )}
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center space-x-4 mb-6">
                <Award size={40} className="text-green-700" />
                <h3 className="text-2xl font-bold text-green-700">
                  {t('Horizon Europe', 'Horizonte Europa')}
                </h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-700 rounded-full mt-2"></div>
                  <span className="text-gray-700">
                    {t('EIC Pathfinder: High-risk, high-impact research', 'EIC Pathfinder: Investigación de alto riesgo y alto impacto')}
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-700 rounded-full mt-2"></div>
                  <span className="text-gray-700">
                    {t('EIC Transition: Technology maturation towards market', 'EIC Transition: Maduración tecnológica hacia el mercado')}
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center space-x-4 mb-6">
                <TrendingUp size={40} className="text-green-700" />
                <h3 className="text-2xl font-bold text-green-700">Next Generation EU</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                {t(
                  'European recovery funds for digital transformation and green transition in the health sector',
                  'Fondos de recuperación europeos destinados a transformación digital y transición verde del sector salud'
                )}
              </p>
            </div>
          </div>

          <div className="mt-12 bg-green-50 border border-green-200 rounded-lg p-8">
            <h3 className="text-xl font-semibold mb-4 text-green-700">
              {t('Competitive Advantages', 'Ventajas Competitivas')}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-gray-700 mb-2 font-medium">
                  {t('✓ Strategic academic alliances', '✓ Alianzas académicas estratégicas')}
                </p>
                <p className="text-sm text-gray-600">
                  {t('University of Málaga (UMA) and Cancer Research Center (CIC) Salamanca', 'Universidad de Málaga (UMA) y Centro de Investigación del Cáncer (CIC) Salamanca')}
                </p>
              </div>
              <div>
                <p className="text-gray-700 mb-2 font-medium">
                  {t('✓ National patent filed', '✓ Patente nacional presentada')}
                </p>
                <p className="text-sm text-gray-600">
                  {t('Q1 2025 before OEPM, establishing intellectual property protection', 'Q1 2025 ante OEPM, estableciendo protección de propiedad intelectual')}
                </p>
              </div>
              <div>
                <p className="text-gray-700 mb-2 font-medium">
                  {t('✓ Capital-efficient model', '✓ Modelo eficiente de capital')}
                </p>
                <p className="text-sm text-gray-600">
                  {t('Self-funded initial development (~€35K) demonstrating commitment', 'Desarrollo inicial autofinanciado (~€35K) demostrando compromiso')}
                </p>
              </div>
              <div>
                <p className="text-gray-700 mb-2 font-medium">
                  {t('✓ High socioeconomic impact', '✓ Alto impacto socioeconómico')}
                </p>
                <p className="text-sm text-gray-600">
                  {t('Addressing unmet medical needs and reducing healthcare costs', 'Abordando necesidades médicas no cubiertas y reduciendo costes sanitarios')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-green-700">
            {t('Contact', 'Contacto')}
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            {t(
              'For more information about our project and collaboration opportunities in public funding programs',
              'Para más información sobre nuestro proyecto y oportunidades de colaboración en programas de financiación pública'
            )}
          </p>
          <div className="space-y-4">
            <p className="text-gray-700">
              <span className="font-semibold">{t('Email:', 'Correo Electrónico:')}</span>{' '}
              <a href="mailto:grants@metasigna.company" className="text-green-700 hover:text-green-800">
                grants@metasigna.company
              </a>
            </p>
            <p className="text-gray-700">
              <span className="font-semibold">{t('Address:', 'Dirección:')}</span> Järvevana tee 9, 11314, Tallinn, Estonia
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Grants;
